import Dialog from "@components/dialog";
import FieldsWrapper from "@components/inputs/field/FieldsWrapper";
import SmartField, { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { setDirtyFlag } from "@odata/Data.utils";
import { IPrDepositedDeductionEntity, PrDeductionEntity } from "@odata/GeneratedEntityTypes";
import i18next from "i18next";
import React, { useCallback } from "react";

import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";

interface IProps {
    storage: FormStorage;
    depositedDeductions: IPrDepositedDeductionEntity[];
    onClose: (isConfirmed: boolean) => void;
}

const InsolvencyDepositClosingDialog: React.FC<IProps> = ({ storage, depositedDeductions, onClose }) => {
    const bc = storage.data.bindingContext;
    const closingCodeBc = bc.navigate(PrDeductionEntity.InsolvencyDepositClosingCode);
    const dateLegallyEffectiveBc = bc.navigate(PrDeductionEntity.DateLegallyEffective);

    const handleTemporalChange = useCallback((args: ISmartFieldChange) => {
        storage.handleTemporalChange(args);
        storage.refresh();
    }, [storage]);

    const handleConfirm = useCallback(async () => {
        const errors = await storage.validateFields([closingCodeBc, dateLegallyEffectiveBc]);
        if (errors?.some(e => !!e)) {
            storage.refreshFields();
            return;
        }

        const isConfirmed = await storage.confirmFields([closingCodeBc, dateLegallyEffectiveBc]);
        if (!isConfirmed) {
            storage.refreshFields();
            return;
        }

        const latestDeduction = [...depositedDeductions].sort((a, b) => {
            const dateA = getUtcDayjs().set("month", a.Payslip.Month).set("year", a.Payslip.Year);
            const dateB = getUtcDayjs().set("month", b.Payslip.Month).set("year", b.Payslip.Year);
            return dateB.diff(dateA);
        })[0];

        const dateEnd = getUtcDayjs()
            .set("month", latestDeduction.Payslip.Month)
            .set("year", latestDeduction.Payslip.Year)
            .endOf("month")
            .toDate();

        storage.setValueByPath(PrDeductionEntity.DateEnd, dateEnd, true);
        setDirtyFlag(storage, bc.navigate(PrDeductionEntity.DateEnd));

        onClose(true);
    }, [storage, depositedDeductions, onClose, bc, closingCodeBc, dateLegallyEffectiveBc]);

    const handleCancel = useCallback(() => {
        storage.cancelFields([closingCodeBc, dateLegallyEffectiveBc]);
        onClose(false);
    }, [storage, onClose, closingCodeBc, dateLegallyEffectiveBc]);

    return (
        <Dialog
            title={i18next.t("Deduction:Form.EndDeposit")}
            onConfirm={null}
            onClose={handleCancel}
            footer={
                <ConfirmationButtons
                    confirmText={storage.t("Common:General.Confirm")}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    useWrapper={false}
                />
            }
        >
            <FieldsWrapper isColumn>
                <FieldsWrapper>
                    <SmartField
                        storage={storage}
                        bindingContext={closingCodeBc}
                        onTemporalChange={handleTemporalChange}
                        fieldDef={{ isConfirmable: true }}
                    />
                </FieldsWrapper>
                <FieldsWrapper>
                    <SmartField
                        storage={storage}
                        bindingContext={dateLegallyEffectiveBc}
                        onTemporalChange={handleTemporalChange}
                        fieldDef={{ isConfirmable: true }}
                    />
                </FieldsWrapper>
            </FieldsWrapper>
        </Dialog>
    );
};

export default InsolvencyDepositClosingDialog;
